/** @format */

import React, { useCallback, useState, useEffect } from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import usePortal from 'react-cool-portal';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Keyboard, Controller } from 'swiper';
import { map } from 'lodash';

import srcIconClose from '../../../assets/icons/dovevivo_icon_close_white.png';
import * as styles from '../../../scss/components/gallery.module.scss';

SwiperCore.use([Navigation, Keyboard, Controller]);

export const useGallery = (isPlanimetry = false, options = {}) => {
  const { Portal, ...rest } = usePortal({
    ...options,
    containerId: 'gallery',
    defaultShow: false,
    internalShowHide: false,
    escToHide: false,
  });

  const Gallery = ({
    isShow,
    pictures,
    onClose,
    index = 1,
    opacity = 0.6,
    transition = 0.15,
  }: {
    isShow: boolean;
    pictures: Array<string> | { gatsbyImage: string }[] | any;
    onClose: (index: number) => void;
    index?: number;
    opacity?: number;
    transition?: number;
  }) => {
    const [gallerySwiper, setGallerySwiper] = useState(null);
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [activeSlide, setActiveSlide] = useState(index === 0 ? 1 : index - 1);

    const slideSelected = activeSlide ? activeSlide - 1 : 0;
    const imageSelected = pictures[slideSelected];

    const onCloseHandler = () => {
      onClose(gallerySwiper.activeIndex + 1);
      setGalleryOpen(false);
    };

    const escHandler = (s, k) => {
      if (k === 27) {
        onClose(gallerySwiper.activeIndex + 1);
        setGalleryOpen(false);
      }
    };

    const stopPropagation = e => e.stopPropagation();

    useEffect(() => {
      if (isShow) setGalleryOpen(true);
    }, [isShow]);

    const onActiveIndexChange = useCallback(e => {
      setActiveSlide(e.activeIndex + 1);
    }, []);

    return (
      <Portal>
        <div
          className={`${styles.gallery} ${galleryOpen ? styles.visible : ''}`}
          onClick={onCloseHandler}
          tabIndex={-1}
          style={{
            backgroundColor: `rgba(40, 40, 40, ${opacity})`,
            transition: `opacity ${transition}s, visibility ${transition}s`,
          }}
          aria-hidden
        >
          <div onClick={stopPropagation} className={styles.container} aria-hidden>
            <div style={{ display: 'flex', flexDirection: 'row-reverse', marginBottom: '20px' }}>
              <p className={styles.pictureIndex}>
                {activeSlide || 1}/{pictures.length}
              </p>
              <div onClick={onCloseHandler}>
                <img src={srcIconClose} style={{ width: '20px' }} />
              </div>
            </div>
            <Swiper
              onSwiper={setGallerySwiper}
              controller={{ control: gallerySwiper }}
              navigation
              initialSlide={index - 1}
              keyboard={{
                enabled: true,
                onlyInViewport: false,
              }}
              onKeyPress={escHandler}
              onActiveIndexChange={onActiveIndexChange}
            >
              {map(pictures, picture => {
                if (picture.gatsbyImage) {
                  const image = getImage(picture.gatsbyImage);

                  return (
                    <SwiperSlide key={`gallery-picture-${picture.gatsbyImage.gatsbyImageData.images.fallback.src}`}>
                      <GatsbyImage image={image} className={styles.gatsbyImage} alt="" />
                    </SwiperSlide>
                  );
                }
                return (
                  <SwiperSlide key={`gallery-picture-${picture}`}>
                    <div
                      style={{
                        backgroundImage: `url(${picture})`,
                        transform: isPlanimetry ? 'rotate(-90deg)' : 'none',
                        backgroundSize: 'contain',
                        height: isPlanimetry ? '500px' : 'auto',
                      }}
                      className={styles.galleryImage}
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>
      </Portal>
    );
  };

  return { Gallery, ...rest };
};
